/* eslint-disable react/jsx-no-bind, react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Close,
  DragIndicator,
  PhotoCameraOutlined,
  VideocamOutlined,
} from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MEDIA_TYPES } from '../Task.model';
import colors from '../../colors.styles';
import adminFormStyles from '../../shared/styles/adminForm.styles';

const styles = {
  itemCard: (transform, transition, isDragging) => ({
    backgroundColor: '#f6f6f6',
    borderColor: isDragging ? colors.base.walmart_blue : '#dbe2e6',
    borderRadius: '12px',
    boxShadow: isDragging ? '0 8px 24px rgba(0, 0, 0, 0.14)' : 'none',
    opacity: isDragging ? 0.92 : 1,
    position: 'relative',
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : 1,
  }),
  field: adminFormStyles.field,
  dragHandle: {
    color: '#656d80',
    cursor: 'grab',
    flex: '0 0 40px',
    height: 40,
    touchAction: 'none',
    width: 40,
    '&:active': { cursor: 'grabbing' },
    '&:hover': { backgroundColor: '#e9ecee', color: '#041f41' },
  },
  dragIcon: {
    fontSize: 20,
  },
  indexBadge: {
    alignItems: 'center',
    backgroundColor: colors.base.walmart_blue,
    borderRadius: '50%',
    color: colors.base.light,
    display: 'flex',
    flex: '0 0 20px',
    fontSize: '12px',
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 800,
    height: 20,
    justifyContent: 'center',
  },
  removeButton: {
    color: '#656d80',
    height: 40,
    width: 40,
    '&:hover': { backgroundColor: '#e9ecee', color: '#041f41' },
  },
  closeIcon: {
    fontSize: 18,
  },
  mediaControl: {
    flexShrink: 0,
  },
  fieldLabel: adminFormStyles.fieldLabel,
  mediaLabel: {
    ...adminFormStyles.fieldLabel,
    mb: 0.75,
  },
  toggleGroup: {
    '& .MuiToggleButton-root': {
      borderColor: '#dbe2e6',
      color: '#656d80',
      fontSize: '14px',
      minHeight: 42,
      px: 2,
      textTransform: 'none',
      '&.Mui-selected': {
        backgroundColor: colors.base.walmart_blue,
        color: colors.base.light,
        '&:hover': {
          backgroundColor: colors.base.dark_blue,
        },
      },
    },
  },
  mediaIcon: {
    fontSize: 16,
    mr: 1,
  },
  instructions: {
    width: '100%',
  },
};

export default function SortableTaskContentRequest({
  contentRequest,
  index,
  canRemove,
  errors,
  onRemove,
  onUpdate,
}) {
  const {
    attributes,
    isDragging,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: contentRequest.ui_id });

  return (
    <Paper
      ref={setNodeRef}
      variant="outlined"
      sx={styles.itemCard(transform, transition, isDragging)}
    >
      <Stack spacing="20px" padding="20px">
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton
            ref={setActivatorNodeRef}
            aria-label={`Reorder content item ${index + 1}`}
            sx={styles.dragHandle}
            {...attributes}
            {...listeners}
          >
            <DragIndicator aria-hidden="true" sx={styles.dragIcon} />
          </IconButton>
          <Typography align="center" sx={styles.indexBadge}>
            {index + 1}
          </Typography>
          <TextField
            fullWidth
            label="Content item title"
            placeholder="e.g. Endcap display images"
            value={contentRequest.title}
            onChange={(event) => onUpdate(index, 'title', event.target.value)}
            error={Boolean(errors.title)}
            helperText={errors.title}
            sx={styles.field}
          />
          <IconButton
            aria-label={`Remove content item ${index + 1}`}
            onClick={() => onRemove(index)}
            disabled={!canRemove}
            sx={styles.removeButton}
          >
            <Close sx={styles.closeIcon} />
          </IconButton>
        </Stack>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'flex-start' }}
        >
          <FormControl sx={styles.mediaControl}>
            <FormLabel sx={styles.mediaLabel}>Media type</FormLabel>
            <ToggleButtonGroup
              exclusive
              value={contentRequest.media_type}
              onChange={(event, value) =>
                value && onUpdate(index, 'media_type', value)
              }
              sx={styles.toggleGroup}
            >
              <ToggleButton value={MEDIA_TYPES.PHOTO} aria-label="Photo">
                <PhotoCameraOutlined sx={styles.mediaIcon} />
                Photo
              </ToggleButton>
              <ToggleButton value={MEDIA_TYPES.VIDEO} aria-label="Video">
                <VideocamOutlined sx={styles.mediaIcon} />
                Video
              </ToggleButton>
            </ToggleButtonGroup>
            {errors.media_type && (
              <FormHelperText error>{errors.media_type}</FormHelperText>
            )}
          </FormControl>

          <Stack spacing={0.75} sx={styles.instructions}>
            <FormLabel
              htmlFor={`content-request-description-${contentRequest.ui_id}`}
              sx={styles.fieldLabel}
            >
              Additional instructions
            </FormLabel>
            <TextField
              fullWidth
              id={`content-request-description-${contentRequest.ui_id}`}
              placeholder="Consider aspect ratios or instructions specific to this content"
              value={contentRequest.description}
              onChange={(event) =>
                onUpdate(index, 'description', event.target.value)
              }
              sx={styles.field}
            />
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
}

SortableTaskContentRequest.propTypes = {
  contentRequest: PropTypes.shape({
    ui_id: PropTypes.string.isRequired,
    id: PropTypes.number,
    title: PropTypes.string,
    description: PropTypes.string,
    media_type: PropTypes.oneOf(['', ...Object.values(MEDIA_TYPES)]),
  }).isRequired,
  index: PropTypes.number.isRequired,
  canRemove: PropTypes.bool.isRequired,
  errors: PropTypes.shape({
    title: PropTypes.string,
    media_type: PropTypes.string,
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};
